import { useSpeckleAuthStore, useSpeckleStore } from '@haskoning/vue-speckle'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useDjango } from '@/composables/useDjango'
import { API } from '../api/apiUrls'
import { useProjectStore } from '@/stores/project'
import { storeToRefs } from 'pinia'
import { useSectionLineUploadCollectionsStore } from '@/stores/sectionLineUploadCollections'
import { useSpeckleIntegrationStore } from '@/stores/speckleIntegration'
import { useDebounceFn } from '@vueuse/core'
import { useSectionLinesStore } from '@/stores/sectionLine'
import { useSpeckleSsoStore } from '@/stores/speckleSso'
import { useGIDataSpeckleImport, type ImportErrors } from '@/composables/useGIDataSpeckleImport'

const { api } = useDjango()

export enum DefaultSpeckleModel {
  IMPORT_SECTION_LINE = 'pace-geoprofile connection',
  EXPORT_SECTION_LINE = 'pace-geoprofile connection',
  IMPORT_GI_DATA = 'gi_dataset'
}

export enum SpeckleMode {
  IMPORT_SECTION_LINE = 'import-section-line',
  EXPORT_SECTION_LINE = 'export-section-line',
  IMPORT_GI_DATA = 'import-gi-data'
}

export function useSpeckleManagement(mode: SpeckleMode) {
  const speckleStore = useSpeckleStore({ autoSelectData: false })
  const sectionLineUploadCollectionName = ref('')
  const giDataUploadCollectionName = ref('')
  const giDataUploadCollectionColor = ref('#FF0000')
  const speckleCommitMessage = ref('Exported from GeoProfile')
  const importingSectionLines = ref(false)
  const exportingSectionLines = ref(false)
  const importingGIData = ref(false)
  const speckleAuthStore = useSpeckleAuthStore()
  const projectStore = useProjectStore()
  const speckleIntegrationStore = useSpeckleIntegrationStore()
  const { currentItem: currentProject } = storeToRefs(projectStore)
  const sectionLineUploadCollectionStore = useSectionLineUploadCollectionsStore()
  const sectionLineStore = useSectionLinesStore()
  const speckleSsoStore = useSpeckleSsoStore()
  const { ssoReauthenticationRequired } = storeToRefs(speckleSsoStore)
  const { importGIData, importProgress } = useGIDataSpeckleImport()

  function resetInputs() {
    sectionLineUploadCollectionName.value = ''
    giDataUploadCollectionName.value = ''
    giDataUploadCollectionColor.value = '#FF0000'
    speckleCommitMessage.value = 'Exported from GeoProfile'
  }

  onMounted(async () => {
    await speckleStore.getAllUserProjects()
  })

  onUnmounted(() => {
    speckleStore.currentSelection.project = undefined
    speckleStore.currentSelection.model = undefined
    speckleStore.currentSelection.version = undefined
  })

  watch(
    () => speckleStore.currentSelection.project,
    async () => {
      await updateLinkedProject()
      await setModelInfo()
      await setSpeckleVersion()
    }
  )

  watch(
    () => speckleStore.allProjects,
    async (newValue) => {
      if (!newValue || newValue.length === 0) {
        return
      }
      autoSelectProject()
    }
  )

  function autoSelectProject() {
    const currentLinkedSpeckleProjectId = projectStore.currentItem.speckle?.project_id
    for (const speckleProject of speckleStore.allProjects) {
      if (speckleProject.id === currentLinkedSpeckleProjectId) {
        speckleStore.currentSelection.project = speckleProject
        return
      }
    }
  }

  const updateLinkedProject = useDebounceFn(async () => {
    const currentLinkedSpeckleProjectId = projectStore.currentItem.speckle?.project_id
    const selectedSpeckleProjectId = speckleStore.currentSelection.project?.id || null

    const currentProjectId = projectStore.currentItem.id
    if (!currentProjectId) {
      return
    }
    if (currentLinkedSpeckleProjectId === selectedSpeckleProjectId) {
      return
    }
    const linkedSpeckleId = projectStore.currentItem.speckle?.id
    if (!linkedSpeckleId) {
      const response = await speckleIntegrationStore.create({
        project_id: selectedSpeckleProjectId,
        linked_model: currentProjectId
      })
      projectStore.currentItem.speckle = response
      return
    }
    const updateData = {
      id: linkedSpeckleId,
      project_id: selectedSpeckleProjectId,
      linked_model: currentProjectId
    }
    await speckleIntegrationStore.update(updateData)
    projectStore.currentItem.speckle = updateData
  }, 300)

  async function setModelInfo() {
    if (!speckleStore.currentSelection.project) {
      speckleStore.currentSelection.model = undefined
      return
    }
    await speckleStore.getSpeckleModels()
    const defaultModelMapping = {
      [SpeckleMode.IMPORT_SECTION_LINE]: DefaultSpeckleModel.IMPORT_SECTION_LINE,
      [SpeckleMode.EXPORT_SECTION_LINE]: DefaultSpeckleModel.EXPORT_SECTION_LINE,
      [SpeckleMode.IMPORT_GI_DATA]: DefaultSpeckleModel.IMPORT_GI_DATA
    }
    const defaultModelName = defaultModelMapping[mode]
    for (const model of speckleStore.allModels) {
      if (model.name === defaultModelName) {
        speckleStore.currentSelection.model = model
        return
      }
    }
    speckleStore.currentSelection.model = undefined
  }

  watch(
    () => speckleStore.currentSelection.model,
    async (newValue) => {
      if (!newValue) {
        return
      }
      await setSpeckleVersion()
    }
  )

  async function setSpeckleVersion() {
    if (!speckleStore.currentSelection.model) {
      speckleStore.currentSelection.version = undefined
      return
    }
    await speckleStore.getSpeckleVersions()
    for (const version of speckleStore.allVersions) {
      speckleStore.currentSelection.version = version
      return
    }
    speckleStore.currentSelection.version = undefined
  }

  const canImportSectionLine = computed(() => {
    return [
      speckleStore.currentSelection.project,
      speckleStore.currentSelection.model,
      speckleStore.currentSelection.version,
      sectionLineUploadCollectionName.value
    ].every(Boolean)
  })

  const canExportSectionLine = computed(() => {
    return [
      speckleStore.currentSelection.project,
      speckleStore.currentSelection.model,
      speckleCommitMessage.value
    ].every(Boolean)
  })

  const canImportGIData = computed(() => {
    return [
      speckleStore.currentSelection.project,
      speckleStore.currentSelection.model,
      speckleStore.currentSelection.version,
      giDataUploadCollectionName.value,
      giDataUploadCollectionColor.value
    ].every(Boolean)
  })

  async function importGIDataFromSpeckle(): Promise<ImportErrors> {
    try {
      importingGIData.value = true
      const data = {
        speckle_token: speckleAuthStore.token,
        speckle_project_id: speckleStore.currentSelection.project?.id,
        speckle_model_id: speckleStore.currentSelection.model?.id,
        speckle_version_id: speckleStore.currentSelection.version?.id,
        project: currentProject.value.id,
        description: `Imported from Speckle with id ${speckleStore.currentSelection.project?.id}`,
        name: giDataUploadCollectionName.value,
        collection_color: giDataUploadCollectionColor.value
      }
      const result = await importGIData(data)
      resetInputs()
      return result?.errors ?? {}
    } finally {
      importingGIData.value = false
    }
  }

  async function exportSectionLinesToSpeckle() {
    if (!canExportSectionLine.value) {
      return
    }
    try {
      exportingSectionLines.value = true
      const response = await api
        .service(API.SECTION_GEO_DATA.SECTION_LINE_UPLOAD_COLLECTIONS.EXPORT_TO_SPECKLE)
        .create({
          speckle_token: speckleAuthStore.token,
          speckle_project_id: speckleStore.currentSelection.project?.id,
          speckle_model_name: speckleStore.currentSelection.model?.name,
          speckle_commit_message: speckleCommitMessage.value,
          project: currentProject.value.id
        })
      sectionLineUploadCollectionStore.toggleExportSectionLineToSpeckleDialog(false)
      resetInputs()
      return response
    } finally {
      exportingSectionLines.value = false
    }
  }

  async function importSectionLinesFromSpeckle() {
    if (!canImportSectionLine.value) {
      return
    }
    try {
      importingSectionLines.value = true
      const response = await api
        .service(API.SECTION_GEO_DATA.SECTION_LINE_UPLOAD_COLLECTIONS.IMPORT_FROM_SPECKLE)
        .create({
          speckle_token: speckleAuthStore.token,
          speckle_project_id: speckleStore.currentSelection.project?.id,
          speckle_model_id: speckleStore.currentSelection.model?.id,
          speckle_version_id: speckleStore.currentSelection.version?.id,
          section_line_collection_name: sectionLineUploadCollectionName.value,
          project: currentProject.value.id
        })
      sectionLineUploadCollectionStore.toggleImportSectionLineFromSpeckleDialog(false)
      sectionLineUploadCollectionStore.addItems([response])
      sectionLineStore.addItems(response.sectionline_set.features)
      resetInputs()
    } finally {
      importingSectionLines.value = false
    }
  }

  const canUseSpeckle = computed(() => {
    return !!speckleStore.canUseSpeckle && !ssoReauthenticationRequired.value
  })

  return {
    sectionLineUploadCollectionName,
    canUseSpeckle,
    currentSelection: speckleStore.currentSelection,
    canImportSectionLine,
    canExportSectionLine,
    canImportGIData,
    importSectionLinesFromSpeckle,
    exportSectionLinesToSpeckle,
    importGIDataFromSpeckle,
    importingSectionLines,
    exportingSectionLines,
    importingGIData,
    importProgress,
    giDataUploadCollectionName,
    giDataUploadCollectionColor,
    speckleCommitMessage
  }
}
